import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { InfoBox } from '@educator/components/InfoBox'
import { useTranslation } from '@mc/i18n'

// sessionStorage (not a query param) so it survives the homepage →
// enrollment-status SSR redirect. Value is set-time epoch ms; see MAX_AGE_MS.
const PAYMENT_UPDATED_FLAG = 'mcxPaymentUpdated'

const AUTO_DISMISS_MS = 6000

// Ignore a stale flag (superseded nav, or a different account reusing the tab).
const MAX_AGE_MS = 30000

export const notifyPaymentUpdated = () => {
  try {
    sessionStorage.setItem(PAYMENT_UPDATED_FLAG, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / SSR) — skip the toast.
  }
}

/** Clear on logout so it can't surface for the next account in a reused tab. */
export const clearPaymentUpdatedNotice = () => {
  try {
    sessionStorage.removeItem(PAYMENT_UPDATED_FLAG)
  } catch {
    // ignore
  }
}

/** Globally mounted in `_app`; shows the confirmation on the post-redirect page. */
export const PaymentUpdatedToast = () => {
  const { t } = useTranslation('@mc/educator')
  const router = useRouter()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const check = () => {
      try {
        const raw = sessionStorage.getItem(PAYMENT_UPDATED_FLAG)
        if (!raw) return
        // Consume even if stale, so it can't fire on a later navigation.
        sessionStorage.removeItem(PAYMENT_UPDATED_FLAG)
        const setAt = Number(raw)
        if (Number.isFinite(setAt) && Date.now() - setAt < MAX_AGE_MS) {
          setVisible(true)
        }
      } catch {
        // ignore
      }
    }

    // Runs on full load and on each client-side navigation (redirect chain).
    check()
    router.events.on('routeChangeComplete', check)
    return () => router.events.off('routeChangeComplete', check)
  }, [router.events])

  useEffect(() => {
    if (!visible) return undefined
    const id = setTimeout(() => setVisible(false), AUTO_DISMISS_MS)
    return () => clearTimeout(id)
  }, [visible])

  if (!visible) return null

  return (
    // tailwind-enabled must be an ancestor of the scoped utilities below.
    <div className='tailwind-enabled'>
      <div className='fixed top-4 left-1/2 -translate-x-1/2 z-[300] w-[calc(100%-2rem)] max-w-125'>
        <InfoBox
          variant='success'
          title={t('account.paymentUpdatedToast.title')}
          description={t('account.paymentUpdatedToast.description')}
        />
      </div>
    </div>
  )
}
